import { useState } from 'react';
import { User, Mail, Phone, Camera, Save, CreditCard, Shield, Clock } from 'lucide-react';
import { colors, Card, Button, LoadingSpinner } from '../shared';
import { useAuth } from '../../contexts/AuthContext';
import CardAssignment from './CardAssignment';

const ROLE_LABELS = {
  tech: 'Technician',
  pm: 'Project Manager',
  admin: 'Administrator',
  demo: 'Demo User',
};

const ROLE_COLORS = {
  tech: { bg: colors.greenDim, color: colors.green },
  pm: { bg: colors.orangeDim, color: colors.orange },
  admin: { bg: colors.yellowDim, color: colors.yellow },
  demo: { bg: 'rgba(128, 128, 128, 0.15)', color: '#888888' },
};

export default function UserProfile({ onClose }) {
  const { userProfile, updateUserProfile, profileLoading, canWrite } = useAuth();
  const isDemoUser = userProfile?.role === 'demo';
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'cards'

  const [formData, setFormData] = useState({
    displayName: userProfile?.displayName || '',
    phone: userProfile?.phone || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserProfile({
        displayName: formData.displayName,
        phone: formData.phone || null,
      });
      setEditing(false);
    } catch (err) {
      console.error('Error saving profile:', err);
      alert('Failed to save profile: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (profileLoading || !userProfile) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  const roleStyle = ROLE_COLORS[userProfile.role] || ROLE_COLORS.tech;

  return (
    <div className="p-4 pb-20 overflow-auto h-full" style={{ background: colors.black }}>
      {/* Header with close button */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold" style={{ color: colors.textPrimary }}>
          My Profile
        </h1>
        {onClose && (
          <button
            onClick={onClose}
            className="px-3 py-1 rounded-lg text-sm"
            style={{ color: colors.textMuted }}
          >
            Close
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('profile')}
          className="flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-all"
          style={{
            background: activeTab === 'profile' ? colors.yellow : colors.cardBg,
            color: activeTab === 'profile' ? colors.black : colors.textSecondary,
          }}
        >
          <User size={16} className="inline mr-2" />
          Profile
        </button>
        <button
          onClick={() => setActiveTab('cards')}
          className="flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-all"
          style={{
            background: activeTab === 'cards' ? colors.yellow : colors.cardBg,
            color: activeTab === 'cards' ? colors.black : colors.textSecondary,
          }}
        >
          <CreditCard size={16} className="inline mr-2" />
          My Cards
        </button>
      </div>

      {activeTab === 'profile' ? (
        <>
          {/* Demo User Notice */}
          {isDemoUser && (
            <Card className="mb-4" style={{ background: 'rgba(128, 128, 128, 0.15)', border: '1px solid #888' }}>
              <p className="text-sm" style={{ color: '#aaa' }}>
                You are viewing as a <strong>demo user</strong>. You can explore the app but cannot make any changes.
              </p>
            </Card>
          )}

          {/* Profile Photo & Basic Info */}
          <Card className="mb-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                {userProfile.photoUrl ? (
                  <img
                    src={userProfile.photoUrl}
                    alt={userProfile.displayName}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: colors.inputBg }}
                  >
                    <User size={32} style={{ color: colors.textMuted }} />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold" style={{ color: colors.textPrimary }}>
                  {userProfile.displayName || 'No name set'}
                </h2>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  {userProfile.email}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1"
                    style={{ background: roleStyle.bg, color: roleStyle.color }}
                  >
                    <Shield size={12} />
                    {ROLE_LABELS[userProfile.role] || userProfile.role}
                  </span>
                  {userProfile.status !== 'active' && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ background: colors.redDim, color: colors.red }}
                    >
                      {userProfile.status}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Editable Info */}
          <Card className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium" style={{ color: colors.textPrimary }}>
                Contact Information
              </h3>
              {!editing && !isDemoUser && (
                <button
                  onClick={() => setEditing(true)}
                  className="text-sm px-3 py-1 rounded-lg"
                  style={{ background: colors.yellowDim, color: colors.yellow }}
                >
                  Edit
                </button>
              )}
            </div>

            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: colors.textSecondary }}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg outline-none"
                    style={{
                      background: colors.inputBg,
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`,
                    }}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: colors.textSecondary }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg outline-none"
                    style={{
                      background: colors.inputBg,
                      color: colors.textPrimary,
                      border: `1px solid ${colors.border}`,
                    }}
                    placeholder="(414) 555-1234"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setEditing(false);
                      setFormData({
                        displayName: userProfile?.displayName || '',
                        phone: userProfile?.phone || '',
                      });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1"
                  >
                    {saving ? <LoadingSpinner size="sm" color={colors.black} /> : <Save size={16} />}
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail size={18} style={{ color: colors.textMuted }} />
                  <span style={{ color: colors.textSecondary }}>{userProfile.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={18} style={{ color: colors.textMuted }} />
                  <span style={{ color: userProfile.phone ? colors.textSecondary : colors.textMuted }}>
                    {userProfile.phone || 'Not set'}
                  </span>
                </div>
              </div>
            )}
          </Card>

          {/* Account Info (read-only) */}
          <Card>
            <h3 className="font-medium mb-4" style={{ color: colors.textPrimary }}>
              Account Information
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span style={{ color: colors.textMuted }}>Member Since</span>
                <span style={{ color: colors.textSecondary }}>
                  {userProfile.createdAt?.toDate
                    ? userProfile.createdAt.toDate().toLocaleDateString()
                    : 'Unknown'}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: colors.textMuted }}>Last Login</span>
                <span style={{ color: colors.textSecondary }}>
                  {userProfile.lastLoginAt?.toDate
                    ? userProfile.lastLoginAt.toDate().toLocaleString()
                    : 'Now'}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: colors.textMuted }}>User ID</span>
                <span style={{ color: colors.textMuted }} className="font-mono text-xs">
                  {userProfile.uid?.slice(0, 12)}...
                </span>
              </div>
            </div>
          </Card>
        </>
      ) : (
        <CardAssignment />
      )}
    </div>
  );
}
